<template>
  <section
    class="relative grid p-1 rounded-8 py-px pr-px"
    :class="[
      !!slots.default ? 'grid-cols-[1fr_40px]' : 'grid-cols-[1fr]',
    ]"
  >
    <div
      class="absolute h-full w-full rounded-8"
      :class="[`bg-${color}`]"
    />
    <div class="z-20 flex select-none items-start w-full overflow-auto will-change-scroll">
      <h5 class="sr-only select-none">
        {{ $t('chat.forwardMessageIsReady') }}
      </h5>
      <div
        class="grid grow gap-y-1 rounded-8 p-2 text-1316 font-light"
        :class="[`bg-${background}`]"
      >
        <p
          class="flex truncate font-normal"
          :class="[`text-${textColor}`]"
        >
          <wui-icon
            name="arrowResendM"
            class="h-4! w-4! mr-1!"
            :class="[`text-${textColor}`]"
          />
          <span class="truncate">{{ forwardingAuthorName }}</span>
        </p>
        <p
          v-if="sanitizedText"
          class="font-normal line-clamp-1 whitespace-pre-wrap break-all ml-2"
        >
          {{ sanitizedText }}
        </p>
        <div
          v-if="file"
          class="mt-2 flex items-start gap-4"
        >
          <picture class="flex h-14 w-14 flex-wrap">
            <file-icon
              :cookie="file.filecookie"
              :mime="file.filemime ?? ''"
              :name="file.filename"
            />
          </picture>
          <p
            :class="[`text-${textColor}`]"
          >
            <template v-if="files.length > 1">
              {{ text }}
            </template>
            <template v-else>
              <p class="break-all">
                {{ file.filename }}
              </p>
            </template>
          </p>
        </div>
      </div>
    </div>
    <slot :color="color" />
  </section>
</template>

<script setup lang="ts">
import { WuiIcon } from '@wui/common-library'
import { IMForwarding } from '@wui/im'
import { computed, useSlots } from 'vue'

import { FileIcon } from '@/entities/chat-event-file'


import { useUserColor, userColorsMap, userTextColorsMap } from '@/shared/composables/user-color'
import { isImageFile } from '@/shared/utils/files'
import { decodeHtmlEntities } from '@/shared/utils/htmlEntitiesDecode'
import { sanitizeToText } from '@/shared/utils/sanitize'

import { resolveForwardingAuthorName } from '../../forward'
import { useMessageFilesPreview } from '../../lib/use-message-files-preview'

const props = defineProps<{
  forwarding: IMForwarding,
}>()


const { getFilesText } = useMessageFilesPreview()
const { getColorByName } = useUserColor()
const slots = useSlots()
const sanitizedText = computed(() => decodeHtmlEntities(sanitizeToText(props.forwarding.forwardingText || '')))
const files = computed(() => props.forwarding.forwardingFiles ?? [])
const forwardingAuthorName = computed(() => resolveForwardingAuthorName(props.forwarding))

const color = computed(() => {
  if (forwardingAuthorName.value) {
    return getColorByName(forwardingAuthorName.value, false)
  }

  return 'primary'
})

const textColor = computed(() => {
  if (color.value === 'primary') {
    return 'primary'
  }
  return userTextColorsMap[color.value] ?? color.value
})

const background = computed(() => {
  if (color.value) {
    return userColorsMap[color.value]
  }

  return 'primary'
})

const file = computed(() => {
  const image = files.value.find((file) => isImageFile(file.filename))
  return image ?? files.value.find((file) => !isImageFile(file.filename))
})

const text = computed(() => {
  return getFilesText(files.value.length)
})

</script>
